'use client'

import clsx from 'clsx'
import {useEffect} from 'react'
import s from './style.module.scss'
import Divider from '../divider'
import {useParams, useSearchParams} from 'next/navigation'
import {usePathname} from 'next/dist/client/components/navigation'
import {useScrollLock} from '@app/hooks/use-scroll-lock'
import {fallbackLang, LanguageCodes, supportedLanguages} from '@app/i18n/settings'
import CloseButton from './close-button'
import TranslationIcon from '@app/components/icons/translate/translation-icon'
import LangLink from './lang-link'

interface Props {
  langTranslate: Record<string, string>
  closeTranslation: () => void
}

export function generateLanguageUrl(
  pathname: string | null,
  lang: string,
  currentLang?: string,
  queryParams?: string,
): string {
  const newUrl =
    pathname === '/' ? `/${lang}` : (pathname || '/').replace(new RegExp(`^\/${currentLang}(\/|$)`), `/${lang}$1`)
  return queryParams ? `${newUrl}?${queryParams}` : newUrl
}

const OverlaySelector = ({langTranslate, closeTranslation}: Props) => {
  const params = useParams<{lang: LanguageCodes}>()
  const pathname = usePathname()
  const qp = useSearchParams()
  const {lockScroll, unlockScroll} = useScrollLock()

  useEffect(() => {
    lockScroll()
    return () => {
      unlockScroll()
    }
  }, [])

  return (
    <div className={s.lang_overlay}>
      <div className={s.lang_switch_content}>
        <div className={s.top_section}>
          <CloseButton onClick={closeTranslation} className='absolute top-20 right-20' />
          <TranslationIcon className='mr-3' />
          <h1>Select Language</h1>
        </div>
        <div className='w-full flex justify-center'>
          <Divider className='mb-30' />
        </div>
        <div className={s.lang_wrap} data-testid='intro-language-popup-sheet'>
          {supportedLanguages.map((l) => {
            let url = generateLanguageUrl(pathname, l, params?.lang, qp?.toString())
            const isActive = l === params?.lang
            return (
              <LangLink
                selectedLanguage={l}
                key={l}
                url={url}
                isActive={isActive}
                className={clsx(s.switch, {[s.active]: isActive}, 'font-normal')}
                currentLang={params?.lang || fallbackLang}
              >
                {langTranslate[l]}
              </LangLink>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default OverlaySelector
